import classNames from 'classnames';
import { type MouseEvent, memo, useCallback, useMemo, useState } from 'react';

import { Ai, Check, Search as SearchIcon, TriangleDown } from '@air/next-icons';
import { DropdownMenu, type DropdownMenuOptionItem, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';

export type SearchCommandModeSelectorProps = {
  className?: string;
  search?: string;
};

export const SearchCommandModeSelector = memo(({ className, search }: SearchCommandModeSelectorProps) => {
  const { canUseSmartSearch, searchType, setSearchType } = usePrivateSearchContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const hasSearchTerm = !!search && search.trim().length > 0;

  const handleSearchIconClick = useCallback(
    (e: MouseEvent) => {
      if (canUseSmartSearch) {
        e.stopPropagation();
        // The dropdown will handle the search type selection
      }
    },
    [canUseSmartSearch],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsDropdownOpen(open);
  }, []);

  const searchTypeOptions: DropdownMenuOptionItem[] = useMemo(
    () => [
      {
        id: 'keyword',
        label: 'Exact search',
        onSelect: () => setSearchType('keyword'),
        type: 'item',
        prefix: <SearchIcon data-testid="DROPDOWN_SEARCH_ICON" className="size-4 text-grey-10" />,
        suffix:
          searchType === 'keyword' ? (
            <Check data-testid="EXACT_SEARCH_CHECK_ICON" className="size-4 text-grey-10" />
          ) : undefined,
      },
      {
        id: 'smart',
        label: <span>Smart search</span>,
        onSelect: () => setSearchType('smart'),
        type: 'item',
        prefix: <Ai data-testid="DROPDOWN_AI_ICON" className="size-4 text-grey-10" />,
        suffix:
          searchType === 'smart' ? (
            <Check data-testid="SMART_SEARCH_CHECK_ICON" className="size-4 text-grey-10" />
          ) : undefined,
      },
    ],
    [setSearchType, searchType],
  );

  const searchIcon = (
    <button
      className={cn(
        'flex cursor-pointer items-center gap-0.5 rounded-full bg-grey-1 px-1 py-0.5 hover:bg-grey-4',
        isDropdownOpen && 'bg-grey-4',
        className,
      )}
      onClick={handleSearchIconClick}
      type="button"
      data-testid="SEARCH_COMMAND_MODE_SELECTOR_BUTTON"
    >
      {searchType === 'smart' ? (
        <Ai
          data-testid="SEARCH_COMMAND_MODE_SELECTOR_AI_ICON"
          className={classNames('m-0.5 size-4 shrink-0', !hasSearchTerm && 'text-grey-10')}
          style={{ color: hasSearchTerm ? '#6E56CF' : undefined }}
        />
      ) : (
        <SearchIcon
          data-testid="SEARCH_COMMAND_MODE_SELECTOR_SEARCH_ICON"
          className="m-0.5 size-4 shrink-0 text-grey-10"
        />
      )}
      <TriangleDown data-testid="TRIANGLE_DOWN_ICON" className="size-4 shrink-0 text-grey-10" />
    </button>
  );

  return canUseSmartSearch ? (
    <DropdownMenu
      trigger={searchIcon}
      align="start"
      side="bottom"
      size="large"
      sideOffset={4}
      open={isDropdownOpen}
      onOpenChange={handleOpenChange}
      className="w-[280px]"
    >
      {renderDropdownItems({ options: searchTypeOptions })}
    </DropdownMenu>
  ) : (
    <SearchIcon data-testid="FALLBACK_SEARCH_ICON" className="size-4 shrink-0 text-grey-10" />
  );
});

SearchCommandModeSelector.displayName = 'SearchCommandModeSelector';
